import type { CampaignAnalytics } from "@/interfaces/campaignAnalytics";
import type { Campaign, CampaignOptionsPage, CampaignOptionsParams } from "@/interfaces/campaing";
import type {
  FundingCheckoutTerms,
  LaunchCampaignInput,
  LaunchCampaignResult,
} from "@/interfaces/review";
import { apiClient } from "@/lib/apiClient";
import type { CampaignStatus } from "@/types/types";

export async function fetchCampaigns(params?: {
  search?: string;
  status?: CampaignStatus | "all";
  sort?: "redeemed";
}): Promise<Campaign[]> {
  const query: Record<string, string> = {};
  if (params?.search) query.search = params.search;
  if (params?.status && params.status !== "all") query.status = params.status;
  if (params?.sort) query.sort = params.sort;

  const { data } = await apiClient.get<Campaign[]>("/campaigns", { params: query });
  return data;
}

export async function fetchCampaignOptions(
  params: CampaignOptionsParams,
): Promise<CampaignOptionsPage> {
  const query: Record<string, string | number> = {};
  if (params.search) query.search = params.search;
  if (params.status) query.status = params.status;
  if (params.sort) query.sort = params.sort;
  if (params.offset !== undefined) query.offset = params.offset;
  if (params.limit !== undefined) query.limit = params.limit;
  if (params.ids && params.ids.length > 0) query.ids = params.ids.join(",");
  if (params.pin && params.pin.length > 0) query.pin = params.pin.join(",");
  if (params.hideUnredeemed) query.hideUnredeemed = "true";

  const { data } = await apiClient.get<CampaignOptionsPage>("/campaigns/options", {
    params: query,
  });
  return data;
}

export async function fetchCampaignAnalytics(campaignId: string): Promise<CampaignAnalytics> {
  const { data } = await apiClient.get<CampaignAnalytics>(`/campaigns/${campaignId}/analytics`);
  return data;
}

// Rename a campaign (title only) — allowed in any state; the card updates optimistically, so the
// response body isn't used.
export async function renameCampaign(id: string, name: string): Promise<void> {
  await apiClient.patch(`/campaigns/${id}`, { name });
}

// Duplicates a campaign as a fresh draft owned by the caller and returns the new campaign's
// list-card shape (so the dashboard's optimistic clone card resolves in place).
export async function cloneCampaign(id: string): Promise<Campaign> {
  const { data } = await apiClient.post<Campaign>(`/campaign-builder/campaigns/${id}/clone`);
  return data;
}

/**
 * What a lifecycle action (pause/resume/cancel/archive/restore) returns — the campaign's NEW derived
 * status, so the caller can re-chip without waiting for a refetch. The services write an overlay
 * column; the displayed status stays derived.
 */
export interface CampaignLifecycleResult {
  success: boolean;
  id: number;
  status: CampaignStatus;
}

/** Pause a running campaign — stops further gift/reminder emails. Manual campaigns can't be paused. */
export async function pauseCampaign(id: string): Promise<CampaignLifecycleResult> {
  const { data } = await apiClient.post<CampaignLifecycleResult>(
    `/campaign-builder/campaigns/${id}/pause`,
  );
  return data;
}

/**
 * Resume a paused campaign. When the send date and/or expiry lapsed during the pause, pass the
 * replacement(s) — the server 422s otherwise, which is the forced date picker's trigger.
 */
export async function resumeCampaign(
  id: string,
  dates?: { scheduledDate?: string | null; expireDate?: string | null },
): Promise<CampaignLifecycleResult> {
  const { data } = await apiClient.post<CampaignLifecycleResult>(
    `/campaign-builder/campaigns/${id}/resume`,
    dates ?? {},
  );
  return data;
}

/** Cancel a scheduled / paused-unsent campaign — terminal; returns its credits to the wallet. */
export async function cancelCampaign(id: string): Promise<CampaignLifecycleResult> {
  const { data } = await apiClient.post<CampaignLifecycleResult>(
    `/campaign-builder/campaigns/${id}/cancel`,
  );
  return data;
}

/** Disable a Universal Link campaign — turns the shared /u/ link off (→ `disabled`). Reversible. */
export async function disableCampaign(id: string): Promise<CampaignLifecycleResult> {
  const { data } = await apiClient.post<CampaignLifecycleResult>(
    `/campaign-builder/campaigns/${id}/disable`,
  );
  return data;
}

/** Enable a disabled Universal Link campaign — turns the shared /u/ link back on (→ `active`). */
export async function enableCampaign(id: string): Promise<CampaignLifecycleResult> {
  const { data } = await apiClient.post<CampaignLifecycleResult>(
    `/campaign-builder/campaigns/${id}/enable`,
  );
  return data;
}

/** The Cancel dialog's "what happens to the money" — a read-only dry-run of the credit return. */
export interface CancelCreditPreview {
  model: "prepaid" | "automated" | "postpaid";
  /** Credits returned to the group's spendable wallet (whole-credit decimal string). */
  heldCredits: string;
  /** An invoice-funded deposit that would be voided (postpaid, unpaid) — removed, not returned. */
  invoicedDepositVoided: string;
  /** The billable ceiling released back from the group's credit limit (postpaid). */
  ceilingReleased: string;
  /** The wallet the credits return to — the group's name, or "your wallet". */
  walletName: string;
  /** Earliest expiry among the returning credits' lots (ISO), or null when not lot-backed. */
  earliestExpiry: string | null;
  /** Of heldCredits, how many have already passed expiry (they go straight to the beneficiary). */
  expiredCredits: string;
}

/** Fetch the Cancel dialog's money breakdown before the client confirms. */
export async function fetchCancelPreview(
  id: string,
  path = `/campaign-builder/campaigns/${id}/cancel-preview`,
): Promise<CancelCreditPreview> {
  const { data } = await apiClient.get<{ success: boolean; preview: CancelCreditPreview }>(path);
  return data.preview;
}

/** Archive a finished (closed/canceled/paused-expired) campaign — hidden from the default list. */
export async function archiveCampaign(id: string): Promise<CampaignLifecycleResult> {
  const { data } = await apiClient.post<CampaignLifecycleResult>(
    `/campaign-builder/campaigns/${id}/archive`,
  );
  return data;
}

/** Un-archive — returns the campaign to the lifecycle status it had before it was archived. */
export async function restoreCampaign(id: string): Promise<CampaignLifecycleResult> {
  const { data } = await apiClient.post<CampaignLifecycleResult>(
    `/campaign-builder/campaigns/${id}/restore`,
  );
  return data;
}

/**
 * Launches the campaign — the real call behind "Confirm & pay" (DIV-7811).
 *
 * One transaction server-side: campaign row (campaign_type = prepaid) → contacts → budget
 * (credits + causes) → commitments. A refusal — e.g. the group's FREE balance can't cover the
 * budget — arrives as a 422 whose `error` sentence names the ask and the available; surface it
 * with `apiErrorMessage()` rather than axios's own wording.
 */
export async function launchCampaign(input: LaunchCampaignInput): Promise<LaunchCampaignResult> {
  const { data } = await apiClient.post<{ success: true } & LaunchCampaignResult>(
    "/campaign-builder/launch",
    input,
  );
  return data;
}

/** What Step 4 prices the funding models on: the pay-as-you-give terms and the postpaid offer. */
export async function fetchFundingTerms(): Promise<FundingCheckoutTerms> {
  const res = await apiClient.get("/campaign-builder/funding-terms");
  return res.data.data as FundingCheckoutTerms;
}
